import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { formatCurrency } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { 
  Wallet, 
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Calendar,
  TrendingDown,
  Info,
  AlertCircle
} from "lucide-react";

// Unified status logic based on safe_amount
export const getUnifiedStatus = (safeAmount, shortfall = 0, buffer = 100) => {
  if (shortfall > 0 || safeAmount <= 0) {
    return {
      status: "shortfall",
      message: "You're short this week",
      bg: "bg-gradient-to-br from-red-500/10 to-red-600/5",
      cardBg: "bg-red-50",
      border: "border-red-200/50",
      textColor: "text-red-600",
      iconColor: "text-red-500",
      Icon: AlertTriangle,
      heroGradient: "from-red-500 to-red-600"
    };
  }
  if (safeAmount > 0 && safeAmount < buffer) {
    return {
      status: "caution",
      message: "Keep an eye on spending",
      bg: "bg-gradient-to-br from-amber-500/10 to-amber-600/5",
      cardBg: "bg-amber-50",
      border: "border-amber-200/50",
      textColor: "text-amber-600",
      iconColor: "text-amber-500",
      Icon: AlertTriangle,
      heroGradient: "from-amber-500 to-amber-600"
    };
  }
  return {
    status: "safe",
    message: "You're in control",
    bg: "bg-gradient-to-br from-emerald-500/10 to-emerald-600/5",
    cardBg: "bg-emerald-50",
    border: "border-emerald-200/50",
    textColor: "text-emerald-600",
    iconColor: "text-emerald-500",
    Icon: CheckCircle,
    heroGradient: "from-emerald-500 to-emerald-600"
  };
};

export default function SafeToSpendCard({ 
  className = "", 
  startingBalance: propStartingBalance,
  onStatusChange,
  onViewShortfall
}) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBreakdown, setShowBreakdown] = useState(false);

  const currency = user?.currency || 'GBP';
  const startingBalance = propStartingBalance ?? user?.starting_balance ?? 0;

  const fetchSafeToSpend = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/dashboard/safe-to-spend?starting_balance=${startingBalance}`, 
        { withCredentials: true }
      );
      setData(response.data);
      
      if (onStatusChange && response.data) {
        const status = getUnifiedStatus(
          response.data.safe_to_spend || response.data.safe_amount, 
          response.data.shortfall
        );
        onStatusChange(status);
      }
    } catch (error) {
      console.error("Error fetching safe to spend:", error);
    } finally {
      setLoading(false);
    }
  }, [startingBalance, onStatusChange]);

  useEffect(() => {
    fetchSafeToSpend();
  }, [fetchSafeToSpend]);

  if (loading) {
    return (
      <div className={`bg-white rounded-2xl p-6 shadow-sm border border-p2p-border/30 animate-pulse ${className}`}>
        <div className="text-center">
          <div className="h-4 bg-p2p-surface rounded w-24 mx-auto mb-3"></div>
          <div className="h-10 bg-p2p-surface rounded w-32 mx-auto mb-2"></div>
          <div className="h-3 bg-p2p-surface rounded w-20 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const safeAmount = data.safe_to_spend ?? data.safe_amount ?? 0;
  const shortfall = data.shortfall ?? 0;
  const hasShortfall = shortfall > 0;
  const firstRiskDate = data.first_risk_date;
  const breakdown = data.breakdown || {};
  const dailyProjection = data.daily_projection || [];
  const overdueUnconfirmedCount = data.overdue_unconfirmed_count || 0;
  
  const statusStyle = getUnifiedStatus(safeAmount, shortfall);
  const StatusIcon = statusStyle.Icon;

  // Calculate daily amount (safe amount / days remaining this week)
  const daysRemaining = 7;
  const dailyAmount = safeAmount > 0 ? safeAmount / daysRemaining : 0;

  // Check what's covered
  const billsCovered = !hasShortfall;
  const debtsCovered = !hasShortfall; // Simplified - debts are part of bills

  return (
    <div 
      className={`bg-white rounded-2xl shadow-sm border border-p2p-border/30 overflow-hidden ${className}`}
      data-guide="safe-to-spend"
      data-testid="safe-to-spend-card"
    >
      {/* Hero Section */}
      <div className={`p-6 ${statusStyle.bg}`}>
        <div className="text-center">
          {/* Label */}
          <div className="flex items-center justify-center gap-2 mb-2">
            <Wallet className={`w-4 h-4 ${statusStyle.iconColor}`} />
            <span className="text-sm font-medium text-p2p-gray uppercase tracking-wide">
              Safe to Spend
            </span>
          </div>

          {/* Main Amount */}
          <div className="mb-2">
            <span className={`text-4xl sm:text-5xl font-bold ${statusStyle.textColor}`}>
              {formatCurrency(safeAmount, currency)}
            </span>
          </div>

          {/* Period */}
          <p className="text-sm text-p2p-muted mb-3">
            next 7 days
          </p>

          {/* Shortfall Warning OR Daily Amount */}
          {hasShortfall ? (
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-100 rounded-full">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <span className="text-sm font-medium text-red-700">
                  You&apos;re {formatCurrency(shortfall, currency)} short
                </span>
              </div>
              
              {firstRiskDate && (
                <p className="text-sm text-red-600">
                  Risk begins {firstRiskDate.formatted}
                </p>
              )}
              
              <Button
                size="sm"
                variant="outline"
                className="mt-2 border-red-300 text-red-600 hover:bg-red-50"
                onClick={onViewShortfall}
                data-testid="view-shortfall-btn"
              >
                See what needs fixing
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {dailyAmount > 0 && (
                <p className="text-sm text-p2p-muted">
                  {formatCurrency(dailyAmount, currency)}/day until next week
                </p>
              )}
              
              {/* Coverage Indicators */}
              <div className="flex items-center justify-center gap-4 text-xs">
                {billsCovered && (
                  <div className="flex items-center gap-1 text-emerald-600">
                    <CheckCircle className="w-3 h-3" />
                    <span>Bills covered</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Expandable Breakdown */}
      <div className="border-t border-p2p-border/30">
        <button
          onClick={() => setShowBreakdown(!showBreakdown)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-p2p-surface/30 transition-colors"
          data-testid="toggle-breakdown-btn"
        >
          <span className="text-sm text-p2p-muted">
            See how this is calculated
          </span>
          {showBreakdown ? (
            <ChevronUp className="w-4 h-4 text-p2p-muted" />
          ) : (
            <ChevronDown className="w-4 h-4 text-p2p-muted" />
          )}
        </button>

        {showBreakdown && (
          <div className="px-4 pb-4 space-y-3">
            {/* Calculation Breakdown */}
            <div className="bg-p2p-surface/50 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-p2p-muted">Available money</span>
                <span className="font-medium text-p2p-gray">
                  {formatCurrency(breakdown.starting_balance || 0, currency)}
                </span>
              </div>
              
              {(breakdown.income_this_period || 0) > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span>+ Income before Sunday</span>
                  <span className="font-medium">
                    +{formatCurrency(breakdown.income_this_period, currency)}
                  </span>
                </div>
              )}
              
              {(breakdown.bills_this_period || 0) > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>− Bills before Sunday</span>
                  <span className="font-medium">
                    −{formatCurrency(breakdown.bills_this_period, currency)}
                  </span>
                </div>
              )}
              
              {(breakdown.overdue_still_due || 0) > 0 && (
                <div className="flex justify-between text-amber-600">
                  <span>− Outstanding commitments</span>
                  <span className="font-medium">
                    −{formatCurrency(breakdown.overdue_still_due, currency)}
                  </span>
                </div>
              )}
              
              <div className="border-t border-p2p-border/50 pt-2 mt-2">
                <div className="flex justify-between font-semibold">
                  <span className="text-p2p-gray">Safe to Spend</span>
                  <span className={statusStyle.textColor}>
                    {formatCurrency(safeAmount, currency)}
                  </span>
                </div>
              </div>
            </div>

            {/* Overdue Unconfirmed Note */}
            {overdueUnconfirmedCount > 0 && (
              <div className="flex items-start gap-2 text-xs text-amber-600 bg-amber-50 rounded-lg p-3">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                                {overdueUnconfirmedCount} past bill{overdueUnconfirmedCount > 1 ? 's' : ''} need{overdueUnconfirmedCount === 1 ? 's' : ''} confirmation
                </span>
              </div>
            )}

            {/* 7-Day Projection Mini Chart */}
            {dailyProjection.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-p2p-muted mb-2">7-day projection</p>
                <div className="flex gap-1">
                  {dailyProjection.map((day, idx) => {
                    const isNegative = day.intraday_low < 0;
                    const hasActivity = day.income > 0 || day.outgoing > 0;
                    
                    return (
                      <div 
                        key={day.date} 
                        className="flex-1 text-center"
                        title={`${day.day_name}: ${formatCurrency(day.end_balance, currency)}`}
                      >
                        <div 
                          className={`h-8 rounded-sm ${
                            isNegative ? 'bg-red-400' : 
                            hasActivity ? 'bg-emerald-400' : 'bg-p2p-surface'
                          }`}
                          style={{ 
                            opacity: hasActivity ? 1 : 0.3
                          }}
                        />
                        <span className="text-[10px] text-p2p-muted mt-1 block">
                          {day.day_name.slice(0, 2)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Shortfall Transactions */}
            {hasShortfall && data.shortfall_transactions && data.shortfall_transactions.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-red-600 font-medium mb-2">Transactions causing shortfall:</p>
                <div className="space-y-1">
                  {data.shortfall_transactions.slice(0, 3).map((tx, idx) => (
                    <div key={idx} className="flex justify-between text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                      <span>{tx.name}</span>
                      <span className="font-medium">{formatCurrency(tx.amount, currency)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
